import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Input,
  Button,
  Title,
  CheckBox,
  Bar,
  Label,
  TextArea,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  BusyIndicator,
  Text,
  Breadcrumbs,
  BreadcrumbsItem
} from '@ui5/webcomponents-react';
import "@ui5/webcomponents-icons/dist/save";
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import Layout from '../Layout';
import usePermission from '../../hooks/usePermission';
import withPermission from '../withPermission';

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const permissionNameMap = {
  home: 'Inicio e informes',
  buyorder: 'Orden de compra',
  salesorder: 'Orden de venta',
  notification: 'Notificación',
  virtualramp: 'Rampa virtual',
  schedules: 'Horarios',
  users: 'Usuarios',
  roles: 'Roles',
  trucks: 'Camiones',
  truckstypes: 'Tipos de camiones',
  district: 'Distrito',
  centers: 'Centros',
  warehouses: 'Almacenes',
  companies: 'Compañías',
  grn: 'GRN',
  branding: 'Marcas',
  saleschannel: 'Canales de venta',
  shippingmethod: 'Método de Envío',
  settings: 'Configuración'
};

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { hasPermission } = usePermission();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [permissions, setPermissions] = useState([]);
  const [formData, setFormData] = useState({
    roleId: '',
    roleName: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Always load the permissions catalog for the checkbox list
        const catalogRes = await axiosInstance.get('permissions');
        if (catalogRes.data) setPermissions(catalogRes.data);

        if (isEdit) {
          // Edit: load role data (includes role's existing permissions with CRUD flags)
          const roleRes = await axiosInstance.get(`roles/${id}`);
          if (roleRes.data) {
            setFormData({
              roleId: roleRes.data.id,
              roleName: roleRes.data.roleName,
              description: roleRes.data.description || '',
              permissions: roleRes.data.permissions || []
            });
          }
        }
        // Create: catalog already loaded, formData.permissions starts empty (no pre-checked)
      } catch (err) {
        console.error('Error al cargar los datos:', err);
        setError('Error al cargar los datos: ' + (err.message || 'Error desconocido'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePermissionChange = (permissionId, action) => {
    const currentPermissions = [...(formData.permissions || [])];
    const permissionIndex = currentPermissions.findIndex(p => p.id === permissionId);
    const permission = permissions.find(p => p.id === permissionId);

    if (!permission) return;

    if (permissionIndex === -1) {
      currentPermissions.push({
        id: permission.id,
        permissionName: permission.permissionName,
        listView: action === 'listView',
        create: action === 'create',
        update: action === 'update',
        delete: action === 'delete'
      });
    } else {
      currentPermissions[permissionIndex] = {
        ...currentPermissions[permissionIndex],
        [action]: !currentPermissions[permissionIndex][action]
      };

      const hasAnyPermission = ['listView', 'create', 'update', 'delete'].some(
        a => currentPermissions[permissionIndex][a]
      );
      if (!hasAnyPermission) {
        currentPermissions.splice(permissionIndex, 1);
      }
    }

    setFormData(prev => ({ ...prev, permissions: currentPermissions }));
  };

  const isPermissionChecked = (permissionId, action) => {
    const permission = formData.permissions?.find(p => p.id === permissionId);
    return permission ? permission[action] : false;
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.roleName?.trim()) {
      errors.roleName = 'El nombre del rol es obligatorio';
    }
    if (formData.permissions.length === 0) {
      errors.permissions = 'Debe asignar al menos un permiso al rol';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (isEdit && !hasPermission('roles', 'update')) {
      setError('No tiene permisos para actualizar roles');
      return;
    }
    if (!isEdit && !hasPermission('roles', 'create')) {
      setError('No tiene permisos para crear roles');
      return;
    }
    if (!validateForm()) return;

    try {
      const dataToSend = {
        roleId: formData.roleId,
        roleName: formData.roleName.trim(),
        description: formData.description,
        permissions: formData.permissions.map(perm => ({
          id: perm.id,
          permissionName: perm.permissionName,
          listView: perm.listView || false,
          create: perm.create || false,
          update: perm.update || false,
          delete: perm.delete || false
        }))
      };

      if (isEdit) {
        await axiosInstance.put(`roles/${id}`, dataToSend);
      } else {
        await axiosInstance.post('roles', dataToSend);
      }

      Toast.fire({
        icon: 'success',
        title: isEdit ? 'Rol actualizado exitosamente' : 'Rol creado exitosamente'
      });
      navigate('/roles');
    } catch (err) {
      console.error('Error:', err);
      setError(err.response?.data?.message || `Error al ${isEdit ? 'actualizar' : 'crear'} el rol`);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <BusyIndicator active size="Large" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '1.5rem' }}>

        {/* Breadcrumb */}
        <Breadcrumbs style={{ marginBottom: '1rem' }}>
          <BreadcrumbsItem onClick={() => navigate('/home')}>Inicio</BreadcrumbsItem>
          <BreadcrumbsItem onClick={() => navigate('/roles')}>Lista de roles</BreadcrumbsItem>
          <BreadcrumbsItem>{isEdit ? 'Editar Rol' : 'Crear Rol'}</BreadcrumbsItem>
        </Breadcrumbs>

        <Title level="H3" style={{ marginBottom: '1.5rem' }}>
          {isEdit ? 'Editar Rol' : 'Crear Rol'}
        </Title>

        {/* Datos del Rol */}
        <div style={{
          background: 'var(--sapGroup_ContentBackground)',
          border: '1px solid var(--sapGroup_ContentBorderColor)',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem' }}>
            <div>
              <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>
                Nombre del Rol
              </Label>
              <Input
                name="roleName"
                value={formData.roleName}
                onInput={handleInputChange}
                valueState={formErrors.roleName ? 'Error' : 'None'}
                style={{ width: '100%' }}
                placeholder="Ej: SUPERVISOR"
              />
              {formErrors.roleName && (
                <Text style={{ color: 'var(--sapNegativeTextColor)', fontSize: '0.75rem', marginTop: '0.25rem', display: 'block' }}>
                  {formErrors.roleName}
                </Text>
              )}
            </div>
            <div>
              <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Descripción</Label>
              <TextArea
                name="description"
                value={formData.description}
                onInput={handleInputChange}
                style={{ width: '100%' }}
                rows={3}
                placeholder="Descripción opcional del rol..."
              />
            </div>
          </div>
        </div>

        {/* Tabla de Permisos */}
        <div style={{
          background: 'var(--sapGroup_ContentBackground)',
          border: '1px solid var(--sapGroup_ContentBorderColor)',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <Title level="H5" style={{ marginBottom: '1rem' }}>Permisos</Title>

          {formErrors.permissions && (
            <div style={{
              background: 'var(--sapNegativeBackground)',
              border: '1px solid var(--sapNegativeColor)',
              borderRadius: '4px',
              padding: '0.5rem 1rem',
              marginBottom: '1rem'
            }}>
              <Text style={{ color: 'var(--sapNegativeTextColor)', fontSize: '0.875rem' }}>
                {formErrors.permissions}
              </Text>
            </div>
          )}

          {permissions.length === 0 ? (
            <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
              No hay permisos disponibles en el sistema.
            </Text>
          ) : (
            <div style={{
              border: '1px solid var(--sapList_BorderColor)',
              borderRadius: '6px',
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 480px)',
              overflowY: 'auto'
            }}>
              {/* Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr repeat(4, 1fr)',
                padding: '0.6rem 1rem',
                background: 'var(--sapList_HeaderBackground)',
                borderBottom: '2px solid var(--sapList_BorderColor)',
                fontWeight: '600',
                fontSize: '0.875rem'
              }}>
                <div>Función</div>
                <div style={{ textAlign: 'center' }}>Listar/Ver</div>
                <div style={{ textAlign: 'center' }}>Crear</div>
                <div style={{ textAlign: 'center' }}>Actualizar</div>
                <div style={{ textAlign: 'center' }}>Borrar</div>
              </div>

              {/* Rows */}
              {permissions.map((permission, idx) => (
                <div key={permission.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr repeat(4, 1fr)',
                  padding: '0.5rem 1rem',
                  borderBottom: idx < permissions.length - 1 ? '1px solid var(--sapList_BorderColor)' : 'none',
                  alignItems: 'center',
                  background: idx % 2 === 0 ? 'var(--sapList_Background)' : 'var(--sapList_AlternatingBackground)',
                  transition: 'background 0.15s'
                }}>
                  <div style={{ fontWeight: '500', fontSize: '0.9rem' }}>
                    {permissionNameMap[permission.permissionName] || permission.permissionName}
                  </div>
                  {['listView', 'create', 'update', 'delete'].map(action => (
                    <div key={action} style={{ textAlign: 'center' }}>
                      <CheckBox
                        checked={isPermissionChecked(permission.id, action)}
                        onChange={() => handlePermissionChange(permission.id, action)}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <Bar design="Footer" style={{ borderRadius: '8px' }}>
          <Button
            slot="endContent"
            design="Transparent"
            onClick={() => navigate('/roles')}
          >
            Cancelar
          </Button>
          {((isEdit && hasPermission('roles', 'update')) || (!isEdit && hasPermission('roles', 'create'))) && (
            <Button
              slot="endContent"
              design="Emphasized"
              icon="save"
              onClick={handleSubmit}
            >
              {isEdit ? 'Guardar Cambios' : 'Crear Rol'}
            </Button>
          )}
        </Bar>

        {error && (
          <MessageBox
            open={!!error}
            onClose={() => setError(null)}
            title="Error"
            type={MessageBoxType.ERROR}
            actions={[MessageBoxAction.OK]}
          >
            {error}
          </MessageBox>
        )}
      </div>
    </Layout>
  );
};

export default withPermission(RoleForm, 'roles', 'listView');
